import { Button } from '@/app/common/components/button/Button'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { PlusIcon } from '@/app/common/icons/arrows'
import { parseRequestError } from '@/app/common/utilities'
import { usePutProgramCatalogCategory } from '@/queries/program_catalog/hooks/usePutProgramCatalogCategory'
import { ProgramCategory } from '@/queries/program_catalog/schema'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useState } from 'react'
import { useSelector } from 'react-redux'

type Props = {
  programCategory: ProgramCategory
  onEnableCategorySuccess: () => void
}

export default function EnableProgramCategoryPopup(props: Props) {
  const { onEnableCategorySuccess, programCategory } = props
  const [isOpen, setIsOpen] = useState(false)
  const programId = useSelector(selectSelectedProgram).id
  const { toast } = useToast()

  const enableCategoryMutation = usePutProgramCatalogCategory({
    onSuccess: () => {
      toast({
        title: 'Category Enabled',
        description: `${programCategory.global_category} has been enabled successfully.`,
        variant: 'default',
      })
      setIsOpen(false)
      onEnableCategorySuccess()
    },
    onError: (error) => {
      toast({
        title: 'Error Enabling Category',
        description:
          parseRequestError(error).message ||
          'There was an error enabling the category.',
        variant: 'destructive',
      })
    },
  })

  // Handle dialog open state changes
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
  }

  // Handle the enable action
  const handleEnableCategory = () => {
    enableCategoryMutation.mutate({
      programId: programId,
      categoryId: programCategory.id,
      status: 1,
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button className="w-full flex items-center gap-2 hover:bg-gray-100 text-left transition rounded px-2 py-2">
          <PlusIcon /> Enable
        </button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Enable</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="text-sm">
            <p>
              Are you sure you want to enable the{' '}
              <strong>{programCategory.global_category_name}</strong> category?
            </p>
            <p className="text-gray-500 mt-2">
              Enabling this category will make it available in your program
              catalog for customers. All items within this category will be
              accessible.
            </p>
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <div>
              <SubmitButton
                onClick={handleEnableCategory}
                status={enableCategoryMutation.status}
                buttonTxt="Enable"
              />
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
